import { useCallback, useEffect } from 'react';
import useLocalState from './useLocalStorage';

interface StoredNext {
  url: string;
  timestamp: number;
}

const STORAGE_KEY = 'resistbot_fallback_next';
const EXPIRY_HOURS = 24;
const EXPIRY_MS = EXPIRY_HOURS * 60 * 60 * 1000;

export function useFallbackNext() {
  const [storedNext, setStoredNext] = useLocalState(STORAGE_KEY, null) as [
    StoredNext | null,
    (value: StoredNext | null) => void,
  ];

  // Store a new fallback next URL
  const storeFallbackNext = useCallback(
    (url: string) => {
      if (!url || url === '/chat') return; // Don't store default or empty URLs

      setStoredNext({
        url,
        timestamp: Date.now(),
      });
    },
    [setStoredNext]
  );

  // Get the stored fallback next URL if it's not expired
  const getFallbackNext = useCallback((): string | null => {
    if (!storedNext) return null;

    const isExpired = Date.now() - storedNext.timestamp > EXPIRY_MS;
    if (isExpired) {
      setStoredNext(null);
      return null;
    }

    return storedNext.url;
  }, [storedNext, setStoredNext]);

  // Clear the stored fallback next URL (call after successful redirect)
  const clearFallbackNext = useCallback(() => {
    setStoredNext(null);
  }, [setStoredNext]);

  // Auto-cleanup expired URLs on hook initialization
  useEffect(() => {
    if (storedNext) {
      const isExpired = Date.now() - storedNext.timestamp > EXPIRY_MS;
      if (isExpired) {
        setStoredNext(null);
      }
    }
  }, [storedNext, setStoredNext]);

  return {
    storeFallbackNext,
    getFallbackNext,
    clearFallbackNext,
  };
}
